import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import whatsappIcon from "../assets/what-icon.png";
import ctaBg from "../assets/what-cta-bg.webp";
import { useContact } from "../hooks/useContact";

// Sampled from the approved mock-up.
const GOLD_RIM = "#E8B84A";
const GOLD_TEXT = "linear-gradient(180deg, #FFE7A3 0%, #F2C35A 45%, #C88A2A 100%)";
// The artwork under a light overlay that keeps the gold heading readable.
const CARD_BG = `linear-gradient(135deg, rgba(55, 3, 5, 0.3), rgba(130, 8, 12, 0.2), rgba(40, 2, 4, 0.45)), url(${ctaBg}) center / cover no-repeat`;
const CARD_GLOW = {
  // Inner double edge: a dark gap, then a fine gold line just inside the ring.
  xs: "0 0 10px rgba(220, 30, 30, 0.4), 0 0 18px rgba(232, 184, 74, 0.16), inset 0 0 0 2px rgba(36, 3, 6, 0.75), inset 0 0 0 3px rgba(255, 214, 130, 0.3), inset 0 0 14px rgba(255, 60, 40, 0.14)",
  md: "0 0 14px rgba(220, 30, 30, 0.5), 0 0 28px rgba(232, 184, 74, 0.22), inset 0 0 0 2px rgba(36, 3, 6, 0.75), inset 0 0 0 3px rgba(255, 214, 130, 0.34), inset 0 0 18px rgba(255, 60, 40, 0.16)",
};
// Static metallic ring: warm-white catchlights on two corners, gold, ruby and crimson between.
const RING = `linear-gradient(135deg, #FFF4D6 0%, ${GOLD_RIM} 10%, #B8862B 22%, #C0161F 38%, #7A0A12 50%, #E0282E 62%, #B8862B 78%, ${GOLD_RIM} 90%, #FFF4D6 100%)`;
const GREEN_BG = "linear-gradient(180deg, #5DBE55 0%, #3FA046 50%, #2E8A36 100%)";
const GREEN_GLOW = "0 0 0 1px rgba(40, 10, 4, 0.85), 0 0 14px rgba(80, 200, 90, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.35), inset 0 -2px 6px rgba(0, 60, 20, 0.35)";
const GREEN_GLOW_HOVER = "0 0 0 1px rgba(40, 10, 4, 0.85), 0 0 20px rgba(90, 220, 100, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.45), inset 0 -2px 6px rgba(0, 60, 20, 0.3)";

interface SupportContactProps {
  /** Lets the drawer close itself when the player leaves for WhatsApp. */
  onClick?: () => void;
}

/** Side-nav support card: heading, one line, and the WhatsApp button to `reviewUrl`. */
const SupportContact = ({ onClick }: SupportContactProps) => {
  const { t } = useTranslation();
  const { reviewUrl, reviewNumber } = useContact();

  return (
    <Box
      sx={{
        position: "relative",
        isolation: "isolate",
        p: "16px 12px",
        textAlign: "center",
        borderRadius: "18px",
        // Transparent so the layout is identical to the old 1px rim; the LED layer draws over it.
        border: "1px solid transparent",
        background: CARD_BG,
        boxShadow: CARD_GLOW,
        "& > :not([aria-hidden])": { position: "relative", zIndex: 1 },
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: "-1px",
          borderRadius: "inherit",
          p: { xs: "1.5px", md: "2px" },
          pointerEvents: "none",
          background: RING,
          // Show only the padding band: the ring.
          mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          maskComposite: "exclude",
          WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
        }}
      />
      <Typography
        sx={{
          fontFamily: "Montserrat, sans-serif",
          fontSize: "16px",
          fontWeight: 800,
          whiteSpace: "nowrap",
          lineHeight: 1.15,
          textTransform: "uppercase",
          background: GOLD_TEXT,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          filter: "drop-shadow(0 0 6px rgba(242, 195, 90, 0.35))",
        }}
      >
        {t("support_24_7")}
      </Typography>
      <Typography sx={{ mt: "4px", mb: "14px", fontSize: "13px", color: "#F5E6D3" }}>
        {t("support_available")}
      </Typography>
      <Button
        component="a"
        href={reviewUrl ?? undefined}
        disabled={!reviewUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("support_whatsapp_label")}
        onClick={onClick}
        fullWidth
        sx={{
          minHeight: "48px",
          px: "10px",
          gap: "8px",
          borderRadius: "14px",
          border: `2px solid ${GOLD_RIM}`,
          background: GREEN_BG,
          boxShadow: GREEN_GLOW,
          color: "#FFFFFF",
          fontSize: "13px",
          fontWeight: 700,
          whiteSpace: "nowrap",
          lineHeight: 1.2,
          textTransform: "none",
          textShadow: "0 1px 1px rgba(0, 50, 15, 0.5)",
          fontVariantNumeric: "tabular-nums",
          transition: "box-shadow 0.25s ease, filter 0.25s ease",
          "&:hover": { background: GREEN_BG, boxShadow: GREEN_GLOW_HOVER, filter: "brightness(1.05)" },
          "&.Mui-focusVisible": { outline: "2px solid #FFE7A3", outlineOffset: "3px" },
          "&.Mui-disabled": { opacity: 0.55, color: "#FFFFFF" },
        }}
      >
        <Box component="img" src={whatsappIcon} alt="" sx={{ width: 24, height: 24, flexShrink: 0, display: "block" }} />
        {reviewNumber || t("support_whatsapp")}
      </Button>
    </Box>
  );
};

export default SupportContact;
